import * as React from 'react';
import '../../Css/Global/Global.css';
import InfoCard from '../Dashboard/InfoCard';
import {Grid } from "@mui/material";

import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';


import TableIcmsProdutos from './TableIcmsProdutos';
import TopChartWithTable from '../../Components/TopChartWithTable';
import ProductDrillDownModal from '../../Components/ProductDrillDownModal';
import { useProductDrillDown } from '../../Hooks/useProductDrillDown';

import { IcmsPorProduto, DebitoIcmsProdutoGrafico } from './types';
import SpedStatusHandler from '../Dashboard/SpedStatusHandler';
  

const AnalisesProduto: React.FC = () => {

    const [loading, setLoading] = React.useState(true);
    const [loadingTop10Debitos, setLoadingTop10Debitos] = React.useState(true);
    const [loadingTop10Creditos, setLoadingTop10Creditos] = React.useState(true);
    const [loadingDetalhamentoIcms, setLoadingDetalhamentoIcms] = React.useState(true);
    const [loadingDetalhamentoIcmsSt, setLoadingDetalhamentoIcmsSt] = React.useState(true);


    const [spedFiscalIdSelecionado, setSpedFiscalIdSelecionado] = React.useState<number | null>(null);

    // Hook para drilldown de produtos
    const {
        isModalOpen: isProductModalOpen,
        drillDownData: productDrillDownData,
        loading: productDrillDownLoading,
        selectedProductName,
        openModal: openProductModal,
        closeModal: closeProductModal,
        error: productDrillDownError
    } = useProductDrillDown();

    
    const [dadosDebitoIcms, setDadosDebitoIcms] = React.useState<{ produto: string; 'ICMS': number; cod_item?: string }[]>([]);
    const [dadosCreditoIcms, setDadosCreditoIcms] = React.useState<{ produto: string; 'ICMS': number; cod_item?: string }[]>([]);

    const [dadosProdutosIcms, setDadosProdutosIcms] = React.useState<IcmsPorProduto[]>([]);
    const [dadosProdutosIcmsSt, setDadosProdutosIcmsSt] = React.useState<IcmsPorProduto[]>([]);

    React.useEffect(() => {
        if (Number(spedFiscalIdSelecionado) > 0) {
            const fetchAllData = async () => {
                // Inicia todos os loadings
                setLoadingDetalhamentoIcms(true);
                setLoadingDetalhamentoIcmsSt(true);
                setLoadingTop10Debitos(true);
                setLoadingTop10Creditos(true);

                try {
                    // Faz todas as requisições em paralelo
                    const [
                        responseIcms,
                        responseIcmsSt,
                        responseDebitos,
                        responseCreditos
                    ] = await Promise.all([
                        fetch(process.env.REACT_APP_API_URL + 'icms-por-produto/' + spedFiscalIdSelecionado),
                        fetch(process.env.REACT_APP_API_URL + 'icms-st-por-produto/' + spedFiscalIdSelecionado),
                        fetch(process.env.REACT_APP_API_URL + 'top-produtos-debitos-icms/' + spedFiscalIdSelecionado),
                        fetch(process.env.REACT_APP_API_URL + 'top-produtos-creditos-icms/' + spedFiscalIdSelecionado)
                    ]);

                    // Processa os dados em paralelo
                    const [dataIcms, dataIcmsSt, dataDebitos, dataCreditos] = await Promise.all([
                        responseIcms.json(),
                        responseIcmsSt.json(),
                        responseDebitos.json(),
                        responseCreditos.json()
                    ]);

                    // Atualiza dados de ICMS
                    if (Array.isArray(dataIcms)) {
                        setDadosProdutosIcms(dataIcms);
                    } else {
                        console.error('Dados de produtos ICMS inválidos:', dataIcms);
                    }

                    // Atualiza dados de ICMS ST
                    if (Array.isArray(dataIcmsSt)) {
                        setDadosProdutosIcmsSt(dataIcmsSt);
                    } else {
                        console.error('Dados de produtos ICMS ST inválidos:', dataIcmsSt);
                    }

                    // Atualiza dados de débitos (com transformação)
                    if (Array.isArray(dataDebitos)) {
                        const formatadoDebitos = dataDebitos.map((item: DebitoIcmsProdutoGrafico) => ({
                            produto: `${item.descr_compl}`,
                            'ICMS': item.total,
                            cod_item: item.cod_item,
                        }));
                        setDadosDebitoIcms(formatadoDebitos);
                    } else {
                        console.error('Dados de débitos inválidos:', dataDebitos);
                    }

                    // Atualiza dados de créditos (com transformação)
                    if (Array.isArray(dataCreditos)) {
                        const formatadoCreditos = dataCreditos.map((item: DebitoIcmsProdutoGrafico) => ({
                            produto: `${item.descr_compl}`,
                            'ICMS': item.total,
                            cod_item: item.cod_item,
                        }));
                        setDadosCreditoIcms(formatadoCreditos);
                    } else {
                        console.error('Dados de créditos inválidos:', dataCreditos);
                    }

                } catch (error) {
                    console.error('Erro ao carregar dados:', error);
                } finally {
                    // Para todos os loadings
                    setLoadingDetalhamentoIcms(false);
                    setLoadingDetalhamentoIcmsSt(false);
                    setLoadingTop10Debitos(false);
                    setLoadingTop10Creditos(false);
                }
            };

            fetchAllData();
        }
    }, [spedFiscalIdSelecionado]);

    // Função para lidar com clique em produto no gráfico
    const handleProductClick = React.useCallback(async (product: any) => {
        if (spedFiscalIdSelecionado && product.cod_item) {
            await openProductModal(
                product.produto || product.descricao_produto || 'Produto',
                product.cod_item,
                spedFiscalIdSelecionado
            );
        }
    }, [spedFiscalIdSelecionado, openProductModal]);

    return (
        <>
            <Grid container spacing={0.5}>

              <SpedStatusHandler
                  onSpedFiscalIdChange={(id) => {
                      setSpedFiscalIdSelecionado(id);
                  }}
              />

              <InfoCard title="Análise de ICMS por Produto" icon={<AddShoppingCartIcon />} iconColor="info" size={12} />

              <Grid item xs={12} md={6} lg={6}>
                <TopChartWithTable
                  data={dadosDebitoIcms}
                  title="Top 10 Débitos de ICMS por Produto"
                  color='#ff5252'
                  loading={loadingTop10Debitos}
                  tableTitle="Demais Produtos com Débitos ICMS"
                  nameKey="produto"
                  valueKey="ICMS"
                  onItemClick={handleProductClick}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <TopChartWithTable
                  data={dadosCreditoIcms}
                  title="Top 10 Créditos de ICMS por Produto"
                  color='#43a047'
                  loading={loadingTop10Creditos}
                  tableTitle="Demais Produtos com Créditos ICMS"
                  nameKey="produto"
                  valueKey="ICMS"
                  onItemClick={handleProductClick}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                  <TableIcmsProdutos 
                    data={dadosProdutosIcms} 
                    loading={loadingDetalhamentoIcms} 
                    title="Detalhamento de Créditos e Débitos por Produto – ICMS"
                    spedFiscalId={spedFiscalIdSelecionado}
                  />
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                  <TableIcmsProdutos 
                    data={dadosProdutosIcmsSt} 
                    loading={loadingDetalhamentoIcmsSt} 
                    title="Detalhamento de Créditos e Débitos por Produto – ICMS ST"
                    spedFiscalId={spedFiscalIdSelecionado}
                  />
              </Grid>
            </Grid>

            {/* Modal de drilldown de produtos */}
            <ProductDrillDownModal
                open={isProductModalOpen}
                onClose={closeProductModal}
                data={productDrillDownData}
                loading={productDrillDownLoading}
                productName={selectedProductName}
            />
        </>
      );

}


export default AnalisesProduto;

